import { useCallback, useEffect, useState } from 'react';
import { adminUserAction, adminUsers } from '@suangua/shared';
import { Avatar } from '../../../components/ui/atoms';
import {
  deadlineText,
  EmptyRow,
  LoadingRow,
  ModerationChoice,
  ModerationModal,
  PageHeader,
  Pagination,
  StatusBadge,
  TableCard,
  useAdminGuard,
  useFromNow,
} from '../parts';

/** 用户列表项 */
type UserItem = Awaited<ReturnType<typeof adminUsers>>['items'][number];
const PAGE_SIZE = 20;

/** 待弹窗处置的目标（0006：封禁/禁言/禁发帖/禁回复/禁换头像均弹窗选择期限） */
interface ModTarget {
  user: UserItem;
  action: 'ban' | 'mute' | 'disable_post' | 'disable_comment' | 'ban_avatar';
  title: string;
  desc: string;
}

/**
 * 管理端 · 用户管理：搜索 / 状态筛选 / 分页
 * 操作（0006 起支持永久或固定天数，弹窗选择，含自定义天数）：
 * 封禁（踢线）/ 解封 / 禁言 / 解禁 / 禁发帖 / 恢复发帖 / 禁回复 / 恢复回复
 * / 移除头像 / 禁止换头像 / 解除禁止换头像。
 */
export function UsersPage() {
  const guard = useAdminGuard();
  const fromNow = useFromNow();
  const [items, setItems] = useState<UserItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState<'' | 'banned'>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tip, setTip] = useState('');
  const [modTarget, setModTarget] = useState<ModTarget | null>(null);

  const load = useCallback(
    async (p = page) => {
      setLoading(true);
      setError('');
      try {
        const res = await adminUsers(keyword, status, p, PAGE_SIZE);
        setItems(res.items);
        setTotal(res.total);
        setPage(res.page);
      } catch (e) {
        setError(guard(e));
      } finally {
        setLoading(false);
      }
    },
    [guard, keyword, status, page],
  );

  useEffect(() => {
    void load(1);
    // 首次与筛选变化时回到第一页
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  /** 执行用户操作并刷新列表（mute_until = 禁言显式截止；choice = 天数/永久期限） */
  async function act(id: number, action: string, choice?: ModerationChoice) {
    setError('');
    setTip('');
    try {
      await adminUserAction(id, action, undefined, choice?.days, choice?.permanent);
      setTip('操作已生效');
      await load(page);
    } catch (e) {
      setError(guard(e));
    }
  }

  /** 弹窗处置提交 */
  function confirmMod(choice: ModerationChoice) {
    if (!modTarget) return;
    void act(modTarget.user.id, modTarget.action, choice);
    setModTarget(null);
  }

  return (
    <div>
      <PageHeader title="用户管理" desc="封禁会立即踢线；各处置支持永久、预设或自定义天数（到期自动恢复）">
        {/* 状态筛选 */}
        <select
          className="rounded-lg border border-stone-200 bg-white px-2.5 py-1.5 text-xs text-stone-600 outline-none focus:border-stone-400"
          value={status}
          onChange={(e) => setStatus(e.target.value as '' | 'banned')}
        >
          <option value="">全部状态</option>
          <option value="banned">仅已封禁</option>
        </select>
        {/* 关键词搜索 */}
        <input
          className="w-56 rounded-lg border border-stone-200 bg-white px-3 py-1.5 text-xs outline-none focus:border-stone-400"
          placeholder="搜索昵称 / 邮箱，回车确认"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void load(1)}
        />
        <button
          onClick={() => void load(1)}
          className="rounded-lg bg-stone-800 px-3.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-stone-700"
        >
          搜索
        </button>
      </PageHeader>

      {error && <p className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600">{error}</p>}
      {tip && <p className="mb-3 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-700">{tip}</p>}

      <TableCard
        footer={<Pagination page={page} size={PAGE_SIZE} total={total} onPage={(p) => void load(p)} />}
      >
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-stone-100 text-left text-xs text-stone-400">
              <th className="px-4 py-2.5 font-medium">用户</th>
              <th className="px-4 py-2.5 font-medium">状态</th>
              <th className="px-4 py-2.5 font-medium">权限</th>
              <th className="px-4 py-2.5 font-medium">注册时间</th>
              <th className="px-4 py-2.5 text-right font-medium">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <LoadingRow />
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <EmptyRow text="没有匹配的用户" />
                </td>
              </tr>
            ) : (
              items.map((u) => {
                const avatarBannedActive = !!u.avatar_banned_until && new Date(u.avatar_banned_until).getTime() > Date.now();
                return (
                <tr key={u.id} className="border-b border-stone-50 last:border-0 hover:bg-stone-50/60">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <Avatar name={u.username} url={u.avatar_url} size="sm" />
                      <div className="min-w-0">
                        <p className="truncate font-medium text-stone-700">
                          {u.username} <span className="ml-1 text-[11px] font-normal text-stone-300">#{u.id}</span>
                        </p>
                        <p className="truncate text-xs text-stone-400">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    {u.status === 'banned' ? (
                      <StatusBadge tone="red">
                        已封禁{u.banned_until ? ` · 至 ${deadlineText(u.banned_until)}` : ' · 永久'}
                      </StatusBadge>
                    ) : (
                      <StatusBadge tone="green">正常</StatusBadge>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-stone-500">
                    <div className="flex flex-wrap items-center gap-1">
                      {u.can_post ? (
                        <StatusBadge tone="gray">可发帖</StatusBadge>
                      ) : (
                        <StatusBadge tone="amber">禁发帖{u.post_banned_until ? ` · 至 ${deadlineText(u.post_banned_until)}` : ' · 永久'}</StatusBadge>
                      )}
                      {u.can_comment ? (
                        <StatusBadge tone="gray">可回复</StatusBadge>
                      ) : (
                        <StatusBadge tone="amber">禁回复{u.comment_banned_until ? ` · 至 ${deadlineText(u.comment_banned_until)}` : ' · 永久'}</StatusBadge>
                      )}
                      {u.mute_until && new Date(u.mute_until).getTime() > Date.now() && (
                        <StatusBadge tone="amber">禁言 · {deadlineText(u.mute_until)}</StatusBadge>
                      )}
                      {avatarBannedActive && <StatusBadge tone="amber">禁换头像 · {deadlineText(u.avatar_banned_until)}</StatusBadge>}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-stone-400">{fromNow(u.created_at)}</td>
                  <td className="max-w-[320px] px-4 py-2.5 text-right text-xs">
                    <div className="flex flex-wrap items-center justify-end gap-y-1">
                      {u.status === 'banned' ? (
                        <ActBtn onClick={() => void act(u.id, 'unban')}>解封</ActBtn>
                      ) : (
                        <ActBtn
                          tone="danger"
                          onClick={() =>
                            setModTarget({
                              user: u,
                              action: 'ban',
                              title: '封禁用户',
                              desc: '封禁后立即踢线并禁止登录；到期自动解封。',
                            })
                          }
                        >
                          封禁
                        </ActBtn>
                      )}
                      <ActBtn
                        onClick={() =>
                          setModTarget({
                            user: u,
                            action: 'mute',
                            title: '禁言',
                            desc: '禁言期间同时禁止发帖与回复，可随时解禁。',
                          })
                        }
                      >
                        {u.mute_until && new Date(u.mute_until).getTime() > Date.now() ? '调整禁言' : '禁言'}
                      </ActBtn>
                      {u.mute_until && new Date(u.mute_until).getTime() > Date.now() && (
                        <ActBtn onClick={() => void act(u.id, 'unmute')}>解禁</ActBtn>
                      )}
                      <ActBtn
                        onClick={() =>
                          u.can_post
                            ? setModTarget({ user: u, action: 'disable_post', title: '禁止发帖', desc: '禁止期内该用户无法发帖，到期自动恢复。' })
                            : void act(u.id, 'enable_post')
                        }
                      >
                        {u.can_post ? '禁发帖' : '恢复发帖'}
                      </ActBtn>
                      <ActBtn
                        onClick={() =>
                          u.can_comment
                            ? setModTarget({ user: u, action: 'disable_comment', title: '禁止回复', desc: '禁止期内该用户无法回复，到期自动恢复。' })
                            : void act(u.id, 'enable_comment')
                        }
                      >
                        {u.can_comment ? '禁回复' : '恢复回复'}
                      </ActBtn>
                      {/* 头像违规处置：移除头像立即生效；禁换头像弹窗选期限 */}
                      {u.avatar_url && (
                        <ActBtn
                          tone="danger"
                          onClick={() => {
                            if (window.confirm(`确认移除「${u.username}」的头像？该操作不可撤销。`)) void act(u.id, 'clear_avatar');
                          }}
                        >
                          移除头像
                        </ActBtn>
                      )}
                      <ActBtn
                        onClick={() =>
                          setModTarget({
                            user: u,
                            action: 'ban_avatar',
                            title: '禁止更换头像',
                            desc: '禁止期内该用户无法设置新头像（恢复默认头像不受限），到期自动恢复。',
                          })
                        }
                      >
                        {avatarBannedActive ? '调整禁换头像' : '禁换头像'}
                      </ActBtn>
                    </div>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </TableCard>

      {/* 期限选择弹窗（永久 / 预设 / 自定义天数） */}
      {modTarget && (
        <ModerationModal
          title={`${modTarget.title}「${modTarget.user.username}」`}
          desc={modTarget.desc}
          onConfirm={confirmMod}
          onClose={() => setModTarget(null)}
          // 解除禁止换头像
          extra={
            modTarget.action === 'ban_avatar' &&
            modTarget.user.avatar_banned_until &&
            new Date(modTarget.user.avatar_banned_until).getTime() > Date.now() ? (
              <button
                onClick={() => {
                  void act(modTarget.user.id, 'unban_avatar');
                  setModTarget(null);
                }}
                className="mt-4 w-full rounded-lg border border-stone-200 py-2 text-xs text-stone-500 hover:bg-stone-50"
              >
                解除禁止（立即允许更换头像）
              </button>
            ) : undefined
          }
        />
      )}
    </div>
  );
}

/** 行内操作按钮 */
function ActBtn(props: { children: React.ReactNode; tone?: 'normal' | 'danger'; onClick: () => void }) {
  const cls =
    props.tone === 'danger'
      ? 'text-red-500 hover:bg-red-50'
      : 'text-stone-500 hover:bg-stone-100';
  return (
    <button onClick={props.onClick} className={`ml-1.5 rounded-md border border-stone-200 px-2 py-1 transition-colors first:ml-0 ${cls}`}>
      {props.children}
    </button>
  );
}
